import { useQuery } from '@tanstack/react-query';
import { useApi, useAuth } from '../../hooks';
import type { Medication } from '../../models/careOSPrototype';

/**
 * Fetches a patient's medications and supplements
 * (GET /private/careos/pharmacy/:patientId/medications).
 *
 * The Pharmacy view seeds local editable state from this and then mutates
 * client-side (add / archive / flag have no backend yet), so consumers
 * read-through with a local override rather than re-seeding on refetch —
 * same posture as `useLabHistory`.
 */
export const useMedications = (patientId: string) => {
  const { api } = useApi();
  const { isAuthenticated, loading, userToken } = useAuth();

  const { data, isLoading, isError } = useQuery<Medication[], Error>({
    queryKey: ['careos-medications', patientId, userToken],
    queryFn: () =>
      api({ route: `/private/careos/pharmacy/${patientId}/medications`, token: userToken }),
    enabled: isAuthenticated && !loading && !!userToken && !!patientId,
    refetchOnWindowFocus: false,
  });

  return { medications: data ?? [], isLoading, isError };
};
